// PreviewModal
import React, { useState } from 'react';
import Modal from 'components/common/Modal';
import styles from './PreviewModal.module.sass';
import Avatar from 'components/common/Avatar';
import Video from 'components/common/Video';
import Icon from 'components/common/Icon';
import useOutsideClick from 'hooks/useOutsideClick';
import Button from 'components/common/Button';
import cn from 'classnames';
import ImageKitImage from 'components/common/ImageKitImage';
import useToast from 'components/common/Toast';
import { getCreatorReferralUrl } from 'utils/creator';
import { trackEvent } from 'services/analytics';
import { useRouter } from 'next/router';
import { CreatorProfileType } from 'types/profile';
import { MediaItem } from 'types/content';

export interface PreviewModalProps {
  creatorId: string;
  profilePicture: string;
  creatorName: string;
  content: MediaItem;
  open: boolean;
  profileType: CreatorProfileType;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const PreviewModal: React.FC<PreviewModalProps> = ({
  creatorId,
  profilePicture,
  creatorName,
  content,
  open,
  profileType,
  onClose,
  onEdit,
  onDelete,
}) => {
  const router = useRouter();
  const referralHref = getCreatorReferralUrl(creatorId);
  const trackCTAClick = () => {
    const id = router.query.id;
    trackEvent(
      `${
        profileType === 'personal' ? 'Creator' : 'Visitor'
      } Clicks Find Me on Trend Button`,
      {
        profileId: id,
        buttonLocation: 'content modal',
        contentId: content.url,
        profileType,
        copy: 'Work with Me',
      }
    );
  };

  return (
    <Modal
      open={open}
      onBackdropClick={onClose}
      className={styles.previewModal}
    >
      <PreviewModalHeaderButtons
        creatorId={creatorId}
        content={content}
        profileType={profileType}
        onEdit={onEdit}
        onDelete={onDelete}
        onClose={onClose}
      />
      <div className={styles.contentCard}>
        <div className={styles.title}>
          <div className={styles.avatarContainer}>
            <Avatar
              type="square"
              src={profilePicture}
              className={styles.avatar}
            />
            <div>
              <h4>{creatorName}</h4>
            </div>
          </div>
          {profileType === 'public' && (
            <Button
              externalLink
              href={referralHref}
              type="primary"
              fullWidth={false}
              onClick={trackCTAClick}
            >
              Work with Me
            </Button>
          )}
        </div>
        <div className={styles.content}>
          {content.type === 'image' ? (
            <ImageKitImage path={content.url} alt={content.url} />
          ) : (
            <Video src={content.url} />
          )}
        </div>
        <div className={styles.mobileDescriptionContainer}>
          <h1>{content.type}</h1>
          <p>{creatorName}</p>
          {profileType === 'public' && (
            <Button
              externalLink
              fullWidth
              href={referralHref}
              type="primary"
              onClick={trackCTAClick}
            >
              Work with Me
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PreviewModal;

const PreviewModalHeaderButtons: React.FC<
  Pick<
    PreviewModalProps,
    'creatorId' | 'content' | 'profileType' | 'onEdit' | 'onDelete' | 'onClose'
  >
> = ({ creatorId, content, profileType, onEdit, onDelete, onClose }) => {
  const { triggerToast } = useToast();
  const [showMenu, setShowMenu] = useState(false);
  const toggleMenu = () => setShowMenu((show) => !show);

  const closeMenu = () => {
    setShowMenu(false);
  };

  const menuRef = useOutsideClick(closeMenu);
  const onCopyLink = () => {
    if (profileType === 'iframe') {
      window.top?.postMessage(
        { action: 'copy', text: content.url },
        process.env.NEXT_PUBLIC_BRAND_WEB_APP as string
      );
    } else {
      navigator.clipboard.writeText(content.url);
    }
    triggerToast('Link Copied to Clipboard');
    trackEvent(
      `${
        profileType === 'personal' ? 'Creator' : 'Visitor'
      } Copies Content Link to Clipboard`,
      {
        contentUrl: content.url,
        creatorId,
        profileType,
      }
    );
    closeMenu();
  };

  return (
    <div className={styles.buttonRow}>
      <div
        className={cn(styles.editMenu, {
          [styles.hide]: !showMenu,
        })}
        ref={menuRef}
      >
        {profileType === 'personal' && (
          <div onClick={onEdit}>
            <Icon name="edit" color="neutral04" />
            Update Title
          </div>
        )}
        <div onClick={onCopyLink}>
          <Icon name="link" color="neutral04" />
          Copy Shareable Link
        </div>
        {profileType === 'personal' && (
          <div onClick={onDelete}>
            <Icon name="trash" color="neutral04" />
            Delete
          </div>
        )}
      </div>
      <span onClick={toggleMenu}>
        <Icon name="ellipsis" color="neutral04" />
      </span>
      <span onClick={onClose}>
        <Icon name="close" color="neutral04" />
      </span>
    </div>
  );
};
